import SEO from "../../../components/SEO";
import { CheckCircle, Plus, Minus } from "lucide-react";
import { useState } from "react";
import img29 from "../../../assets/img29.jpg";
import img10 from "../../../assets/img10.jpg";
import BlogSection from "../../../components/BlogSection";

export default function ChildrenInsurance() {
  const container = "max-w-[1380px] mx-auto px-4 sm:px-6 md:px-10 lg:px-12";
  const [activeIndex, setActiveIndex] = useState(null);
  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqs = [
    {
      question: "Devrais-je souscrire une assurance vie pour mon enfant?",
      answer:
        "La décision de souscrire une assurance vie pour votre enfant dépend des objectifs financiers et des besoins de protection de votre famille. Bien que la plupart des parents n'aient pas besoin d'assurance vie pour les frais finaux, une police d'assurance vie pour enfant peut garantir l'assurabilité dès un jeune âge, protéger contre de futurs problèmes de santé et parfois accumuler de la valeur de rachat avec le temps. Elle peut aussi offrir une tranquillité d'esprit et aider les parents à couvrir des charges financières imprévues advenant l'impensable.",
    },
    {
      question: "À quel âge puis-je souscrire une assurance vie pour mon enfant?",
      answer:
        "L'assurance vie pour enfants peut généralement être souscrite dès la naissance jusqu'à l'adolescence, selon l'assureur. Souscrire tôt une couverture peut permettre de fixer des primes plus basses et de garantir l'assurabilité même si des problèmes de santé surviennent plus tard. Une couverture précoce assure à votre enfant un filet de sécurité financière et procure aux parents la tranquillité d'esprit de savoir que l'avenir de leur enfant est protégé contre les événements imprévus.",
    },
    {
      question: "L'assurance vie pour enfants sert-elle uniquement en cas d'urgence?",
      answer:
        "Non, l'assurance vie pour enfants est bien plus qu'une simple protection d'urgence. Elle peut poser les bases d'une planification financière future, accumuler de la valeur de rachat et garantir l'assurabilité à l'âge adulte. Les parents gagnent en tranquillité d'esprit, sachant que leur enfant dispose d'une protection en place et d'options de soutien financier à long terme, au-delà des événements imprévus.",
    },
    {
      question: "Un enfant peut-il avoir une assurance vie au Canada?",
      answer:
        "Oui, un enfant peut avoir une assurance vie pour enfant au Canada. Ces polices sont conçues spécifiquement pour couvrir les jeunes personnes à charge et n'exigent généralement pas d'examen médical approfondi. Elles offrent une petite prestation de décès pour aider à couvrir des frais imprévus et peuvent permettre une conversion future vers une police permanente plus importante lorsque l'enfant devient adulte. Souscrire une police tôt peut aussi permettre de fixer des primes et une assurabilité plus avantageuses.",
    },
  ];

  return (
    <>
      <SEO
        title="Assurance enfants Canada | Protégez leur avenir – einsured.ca"
        description="Renseignez-vous sur l'assurance pour enfants au Canada. einsured.ca aide les parents à choisir la couverture adéquate pour assurer l'avenir financier de leur enfant."
        keywords="assurance pour enfants, assurance vie juvénile Canada, régime d'accidents étudiants, programme d'assurance apprentissage précoce"
      />

      {/* SECTION HÉRO */}
      <section
        className="relative min-h-[60vh] sm:min-h-[70vh] md:h-[80vh] bg-cover bg-center flex items-center justify-center md:justify-start text-center md:text-left"
        style={{ backgroundImage: `url(${img29})` }}
      >
        <div className="absolute inset-0 bg-black/40"></div>

        <div className={`relative z-10 w-full ${container}`}>
          <div className="text-white max-w-xl">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4">
              Assurance pour enfants
            </h1>
            <p className="text-base sm:text-lg md:text-xl font-semibold mb-8">
              Une assurance pour chaque enfant
            </p>
            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full shadow-md transition"
            >
              Commencer
            </a>
          </div>
        </div>
      </section>

      {/* SECTION INTRODUCTION */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className={container}>
          <div className="max-w-5xl mx-auto text-gray-800">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6">
              Introduction à l'assurance pour enfants
            </h2>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-gray-700 text-center">
              Dans le monde d'aujourd'hui, assurer le bien-être et la sécurité
              de nos enfants est une priorité absolue pour les parents. Bien que
              nous nous efforcions de leur offrir la meilleure éducation, les
              meilleurs soins de santé et les meilleures opportunités, des
              événements imprévus peuvent souvent perturber le parcours que nous
              avons soigneusement planifié pour eux. C'est là qu'intervient
              l'assurance pour enfants, offrant un filet de sécurité qui fournit
              un soutien financier en cas de besoin, assurant que l'avenir de
              nos enfants reste protégé quoi que la vie puisse apporter.
              <span className="block mt-2">
                Comparez les devis d'assurance pour enfants pour trouver la
                meilleure couverture adaptée aux besoins de votre famille.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* POURQUOI L'ASSURANCE POUR ENFANTS EST IMPORTANTE */}
      <section className="py-12 sm:py-16 md:py-2">
        <div className={container}>
          <div className="max-w-6xl mx-auto text-gray-800">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-10 sm:mb-14">
              Pourquoi l'assurance pour enfants est importante
            </h2>

            <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
              {/* Carte 1 */}
              <div className="bg-white p-6 sm:p-8 rounded-3xl border border-gray-200 shadow-sm hover:shadow-md transition">
                <div className="flex items-start gap-3">
                  <CheckCircle
                    aria-hidden="true"
                    className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                    strokeWidth={2.5}
                  />
                  <div>
                    <h3 className="font-bold text-[#49628b] text-lg sm:text-xl mb-2">
                      Protection contre l'imprévu :
                    </h3>
                    <p className="text-base sm:text-lg leading-relaxed text-gray-700">
                      L'assurance pour enfants offre une protection financière
                      en cas de circonstances imprévues telles que des accidents,
                      des maladies, ou même le décès d'un parent ou d'un tuteur.
                      Elle fournit un filet de sécurité essentiel pour s'assurer
                      que les besoins des enfants sont pris en charge, même
                      lorsque les parents ne peuvent plus subvenir à leurs besoins.
                    </p>
                  </div>
                </div>
              </div>

              {/* Carte 2 */}
              <div className="bg-white p-6 sm:p-8 rounded-3xl border border-gray-200 shadow-sm hover:shadow-md transition">
                <div className="flex items-start gap-3">
                  <CheckCircle
                    aria-hidden="true"
                    className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                    strokeWidth={2.5}
                  />
                  <div>
                    <h3 className="font-bold text-[#49628b] text-lg sm:text-xl mb-2">
                      Assurer la continuité des études :
                    </h3>
                    <p className="text-base sm:text-lg leading-relaxed text-gray-700">
                      L'éducation est la clé du succès futur d'un enfant.
                      L'assurance pour enfants peut couvrir les frais de scolarité,
                      garantissant que le parcours académique de votre enfant reste
                      ininterrompu, quelles que soient les difficultés financières
                      que votre famille pourrait rencontrer.
                    </p>
                  </div>
                </div>
              </div>

              {/* Carte 3 */}
              <div className="bg-white p-6 sm:p-8 rounded-3xl border border-gray-200 shadow-sm hover:shadow-md transition">
                <div className="flex items-start gap-3">
                  <CheckCircle
                    aria-hidden="true"
                    className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                    strokeWidth={2.5}
                  />
                  <div>
                    <h3 className="font-bold text-[#49628b] text-lg sm:text-xl mb-2">
                      Couverture médicale :
                    </h3>
                    <p className="text-base sm:text-lg leading-relaxed text-gray-700">
                      Les coûts des soins de santé peuvent être substantiels,
                      surtout en cas de maladies graves ou de blessures.
                      L'assurance pour enfants peut aider à couvrir les frais
                      médicaux, y compris les factures d'hôpital, les traitements
                      et les thérapies, garantissant que votre enfant reçoit les
                      meilleurs soins possibles sans alourdir le fardeau financier
                      de votre famille.
                    </p>
                  </div>
                </div>
              </div>

              {/* Carte 4 */}
              <div className="bg-white p-6 sm:p-8 rounded-3xl border border-gray-200 shadow-sm hover:shadow-md transition">
                <div className="flex items-start gap-3">
                  <CheckCircle
                    aria-hidden="true"
                    className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                    strokeWidth={2.5}
                  />
                  <div>
                    <h3 className="font-bold text-[#49628b] text-lg sm:text-xl mb-2">
                      Sécurité financière pour l'avenir :
                    </h3>
                    <p className="text-base sm:text-lg leading-relaxed text-gray-700">
                      En investissant tôt dans l'assurance pour enfants, vous
                      pouvez assurer l'avenir financier de votre enfant. Certaines
                      polices offrent une accumulation de valeur de rachat ou des
                      options de placement, fournissant une source de fonds pour
                      des étapes importantes telles que les frais de scolarité
                      universitaire ou l'achat d'une première maison.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAMME D'ASSURANCE APPRENTISSAGE PRÉCOCE */}
      <section
        className="relative py-20 sm:py-24 md:py-20 bg-cover bg-center bg-fixed text-white"
        style={{ backgroundImage: `url(${img10})` }}
      >
        <div className="absolute inset-0 bg-sky-900 bg-opacity-75"></div>

        <div className={`relative z-10 w-full ${container}`}>
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-6">
              1. Programme d'assurance apprentissage précoce
            </h2>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center max-w-5xl mx-auto">
              En tant que nouveaux parents, vous avez d'innombrables inquiétudes
              concernant l'avenir et le bien-être de votre petit. Des premiers
              pas aux premiers mots, chaque moment est précieux et mérite d'être
              protégé. C'est pourquoi einsured.ca souhaite vous présenter le
              Programme d'assurance apprentissage précoce, conçu spécifiquement
              pour les enfants de 0 à 36 mois.
            </p>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center max-w-5xl mx-auto mt-4">
              Ce programme est conçu pour offrir une couverture complète qui
              protège la santé et le développement de votre enfant durant ces
              années cruciales de la petite enfance.
            </p>

            <h3 className="text-2xl sm:text-3xl font-bold text-center mt-8 sm:mt-10">
              Obtenez une couverture d'assurance gratuite pour un an !
            </h3>

            <ul className="mt-6 sm:mt-8 space-y-4 text-base sm:text-lg leading-relaxed max-w-5xl mx-auto">
              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-300 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <span>
                  La couverture d'assurance commence à la date la plus tardive
                  parmi les suivantes : le 15e jour de vie de l'enfant ou la date
                  à laquelle la compagnie d'assurance reçoit la demande.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-300 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <span>
                  La période gratuite de 12 mois commence à la date à laquelle
                  la compagnie reçoit la demande.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-300 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <span>
                  Soutien au développement précoce : Nous reconnaissons
                  l'importance de l'éducation précoce et de l'intervention. C'est
                  pourquoi notre programme inclut une couverture pour les examens
                  du développement et les services de soutien éducatif, assurant
                  que votre enfant atteint son plein potentiel.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-300 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <span>
                  un autocollant de voiture GRATUIT et un jouet en peluche
                  Koukou le Koala
                </span>
              </li>
            </ul>

            {/* Mobile */}
            <div className="mt-10 sm:mt-12 md:hidden">
              <div className="space-y-4">
                {/* Rangée 1 */}
                <div className="border border-white/40 bg-white/10 rounded-2xl p-4">
                  <div className="font-bold text-white text-base mb-3">
                    Décès naturel
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="border border-white/30 rounded-xl p-3 bg-white/5">
                      <div className="text-xs uppercase tracking-wide text-white/80 mb-1">
                        Montant de la couverture
                      </div>
                      <div className="font-semibold">2 500 $</div>
                    </div>
                    <div className="border border-white/30 rounded-xl p-3 bg-white/5">
                      <div className="text-xs uppercase tracking-wide text-white/80 mb-1">
                        Période de couverture
                      </div>
                      <div className="font-semibold">Jusqu'à 25 ans</div>
                    </div>
                  </div>
                </div>

                {/* Rangée 2 */}
                <div className="border border-white/40 bg-white/10 rounded-2xl p-4">
                  <div className="font-bold text-white text-base mb-3">
                    Décès accidentel
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="border border-white/30 rounded-xl p-3 bg-white/5">
                      <div className="text-xs uppercase tracking-wide text-white/80 mb-1">
                        Montant de la couverture
                      </div>
                      <div className="font-semibold">10 000 $</div>
                    </div>
                    <div className="border border-white/30 rounded-xl p-3 bg-white/5">
                      <div className="text-xs uppercase tracking-wide text-white/80 mb-1">
                        Période de couverture
                      </div>
                      <div className="font-semibold">À vie</div>
                    </div>
                  </div>
                </div>

                {/* Rangée 3 */}
                <div className="border border-white/40 bg-white/10 rounded-2xl p-4">
                  <div className="font-bold text-white text-base mb-3">
                    Démembrement accidentel
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="border border-white/30 rounded-xl p-3 bg-white/5">
                      <div className="text-xs uppercase tracking-wide text-white/80 mb-1">
                        Montant de la couverture
                      </div>
                      <div className="font-semibold">Jusqu'à 50 000 $</div>
                    </div>
                    <div className="border border-white/30 rounded-xl p-3 bg-white/5">
                      <div className="text-xs uppercase tracking-wide text-white/80 mb-1">
                        Période de couverture
                      </div>
                      <div className="font-semibold">À vie</div>
                    </div>
                  </div>
                </div>

                {/* Rangée 4 */}
                <div className="border border-white/40 bg-white/10 rounded-2xl p-4">
                  <div className="font-bold text-white text-base mb-3">
                    Fracture accidentelle
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="border border-white/30 rounded-xl p-3 bg-white/5">
                      <div className="text-xs uppercase tracking-wide text-white/80 mb-1">
                        Montant de la couverture
                      </div>
                      <div className="font-semibold">Jusqu'à 250 $</div>
                    </div>
                    <div className="border border-white/30 rounded-xl p-3 bg-white/5">
                      <div className="text-xs uppercase tracking-wide text-white/80 mb-1">
                        Période de couverture
                      </div>
                      <div className="font-semibold">À vie</div>
                    </div>
                  </div>
                </div>

                {/* Rangée 5 */}
                <div className="border border-white/40 bg-white/10 rounded-2xl p-4">
                  <div className="font-bold text-white text-base mb-3">
                    Remboursement de certains frais médicaux
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="border border-white/30 rounded-xl p-3 bg-white/5">
                      <div className="text-xs uppercase tracking-wide text-white/80 mb-1">
                        Montant de la couverture
                      </div>
                      <div className="font-semibold">Jusqu'à 1 500 $</div>
                    </div>
                    <div className="border border-white/30 rounded-xl p-3 bg-white/5">
                      <div className="text-xs uppercase tracking-wide text-white/80 mb-1">
                        Période de couverture
                      </div>
                      <div className="font-semibold">À vie</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bureau */}
            <div className="mt-10 sm:mt-12 hidden md:block">
              <table className="w-full border border-white/40 rounded-lg text-left border-collapse">
                <thead>
                  <tr className="bg-white/10 border border-white/40 divide-x divide-white/40">
                    <th className="p-4 font-bold border-r border-white/40">
                      Résumé des garanties
                    </th>
                    <th className="p-4 font-bold border-r border-white/40">
                      Montant de la couverture
                    </th>
                    <th className="p-4 font-bold">Période de couverture</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-white/40 divide-x divide-white/40">
                    <td className="p-4 border-r border-white/40">
                      Décès naturel
                    </td>
                    <td className="p-4 border-r border-white/40">2 500 $</td>
                    <td className="p-4">Jusqu'à 25 ans</td>
                  </tr>
                  <tr className="border-t border-white/40 divide-x divide-white/40">
                    <td className="p-4 border-r border-white/40">
                      Décès accidentel
                    </td>
                    <td className="p-4 border-r border-white/40">10 000 $</td>
                    <td className="p-4">À vie</td>
                  </tr>
                  <tr className="border-t border-white/40 divide-x divide-white/40">
                    <td className="p-4 border-r border-white/40">
                      Démembrement accidentel
                    </td>
                    <td className="p-4 border-r border-white/40">
                      Jusqu'à 50 000 $
                    </td>
                    <td className="p-4">À vie</td>
                  </tr>
                  <tr className="border-t border-white/40 divide-x divide-white/40">
                    <td className="p-4 border-r border-white/40">
                      Fracture accidentelle
                    </td>
                    <td className="p-4 border-r border-white/40">Jusqu'à 250 $</td>
                    <td className="p-4">À vie</td>
                  </tr>
                  <tr className="border-t border-white/40 divide-x divide-white/40">
                    <td className="p-4 border-r border-white/40">
                      Remboursement de certains frais médicaux
                    </td>
                    <td className="p-4 border-r border-white/40">
                      Jusqu'à 1 500 $
                    </td>
                    <td className="p-4">À vie</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-2xl sm:text-3xl font-bold text-center mt-10 sm:mt-12">
              Pour commencer :
            </h3>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center mt-4">
              S'inscrire à notre Programme d'assurance apprentissage précoce est
              simple :
            </p>

            <ul className="mt-6 space-y-4 text-base sm:text-lg leading-relaxed max-w-5xl mx-auto">
              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-300 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <span>
                  <span className="font-semibold">Demander des informations :</span>{" "}
                  Remplissez notre formulaire en ligne pour recevoir plus
                  d'informations et une brochure détaillée sur notre programme.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-300 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <span>
                  <span className="font-semibold">
                    Parler à un spécialiste :
                  </span>{" "}
                  Nos représentants sympathiques sont là pour répondre à vos
                  questions, discuter de vos besoins et vous aider à choisir le
                  plan idéal.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-300 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <span>
                  <span className="font-semibold">S'inscrire et se détendre :</span>{" "}
                  Une fois votre plan sélectionné, l'inscription est rapide et
                  fluide. Détendez-vous ensuite et profitez de ces précieux
                  premiers moments, en sachant que votre enfant est entre de
                  bonnes mains.
                </span>
              </li>
            </ul>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center mt-6">
              Nous sommes là pour vous soutenir à chaque étape. Si vous avez des
              questions ou souhaitez parler à un conseiller, veuillez{" "}
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="blank"
                className="text-sky-300 underline"
              >
                réserver une réunion !
              </a>
            </p>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center">
              Obtenez des devis d'assurance pour enfants personnalisés pour
              sécuriser l'avenir de votre enfant dès aujourd'hui.
            </p>
          </div>
        </div>
      </section>

      {/* 2. Assurance juvénile */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className={container}>
          <div className="max-w-6xl mx-auto text-gray-800">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6">
              2. Assurance juvénile
            </h2>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center max-w-4xl mx-auto">
              Dans un monde rempli d'incertitudes, l'assurance vie juvénile se
              présente comme un phare de protection, promettant non seulement
              la sécurité financière mais aussi la tranquillité d'esprit pour
              l'avenir de nos plus jeunes.
            </p>

            <h3 className="text-2xl sm:text-3xl font-bold text-[#49628b] text-center mt-8 sm:mt-10">
              Comprendre l'assurance vie juvénile
            </h3>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center max-w-4xl mx-auto mt-8">
              L'assurance vie juvénile est une police conçue spécifiquement pour
              les enfants. C'est un accord entre un fournisseur d'assurance et un
              titulaire de police qui offre une prestation de décès dans le cas
              malheureux du décès de l'enfant assuré.
            </p>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center max-w-4xl mx-auto mt-4">
              Au-delà de cela, c'est un investissement dans l'avenir de l'enfant,
              offrant une sécurité financière et des avantages qui s'étendent
              bien dans leur vie adulte.
            </p>

            <h3 className="text-2xl sm:text-3xl font-bold text-[#49628b] text-center mt-10 sm:mt-12">
              Principaux avantages de l'assurance vie juvénile
            </h3>

            {/* pilules */}
            <div className="mt-10 grid md:grid-cols-3 gap-6">
              <div className="bg-[#f2f6fa] rounded-[2rem] shadow-md border border-gray-200 px-6 py-4 flex items-center">
                <span className="inline-block w-3 h-3 rounded-full bg-sky-500 mr-3"></span>
                <span className="text-gray-800 font-medium">
                  Sécurité financière
                </span>
              </div>
              <div className="bg-[#f2f6fa] rounded-[2rem] shadow-md border border-gray-200 px-6 py-4 flex items-center">
                <span className="inline-block w-3 h-3 rounded-full bg-sky-500 mr-3"></span>
                <span className="text-gray-800 font-medium">
                  Assurabilité santé
                </span>
              </div>
              <div className="bg-[#f2f6fa] rounded-[2rem] shadow-md border border-gray-200 px-6 py-4 flex items-center">
                <span className="inline-block w-3 h-3 rounded-full bg-sky-500 mr-3"></span>
                <span className="text-gray-800 font-medium">
                  Croissance de la valeur de rachat
                </span>
              </div>
            </div>

            <div className="mt-6 grid md:grid-cols-2 gap-6">
              <div className="bg-[#f2f6fa] rounded-[2rem] shadow-md border border-gray-200 px-6 py-4 flex items-center">
                <span className="inline-block w-3 h-3 rounded-full bg-sky-500 mr-3"></span>
                <span className="text-gray-800 font-medium">Taux de prime</span>
              </div>
              <div className="bg-[#f2f6fa] rounded-[2rem] shadow-md border border-gray-200 px-6 py-4 flex items-center">
                <span className="inline-block w-3 h-3 rounded-full bg-sky-500 mr-3"></span>
                <span className="text-gray-800 font-medium">
                  Planification future
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Types + Choix */}
      <section className="py-12 sm:py-16 md:py-6 bg-white">
        <div className={container}>
          <div className="max-w-6xl mx-auto text-gray-800">
            <h3 className="text-2xl sm:text-3xl font-bold text-[#49628b] text-center">
              Types de polices d'assurance vie juvénile
            </h3>

            <div className="mt-12 grid md:grid-cols-3 gap-6">
              <div className="bg-[#f2f6fa] p-6 rounded-3xl border border-gray-200 shadow-sm">
                <p className="text-gray-800">
                  <span className="font-bold text-[#49628b]">
                    Assurance vie entière :
                  </span>{" "}
                  Offre une couverture viagère avec des primes fixes et comprend
                  une composante de valeur de rachat qui croît au fil du temps.
                </p>
              </div>
              <div className="bg-[#f2f6fa] p-6 rounded-3xl border border-gray-200 shadow-sm">
                <p className="text-gray-800">
                  <span className="font-bold text-[#49628b]">
                    Assurance vie temporaire :
                  </span>{" "}
                  Offre une couverture pour une période déterminée, généralement
                  10 à 20 ans, sans avantages de valeur de rachat.
                </p>
              </div>
              <div className="bg-[#f2f6fa] p-6 rounded-3xl border border-gray-200 shadow-sm">
                <p className="text-gray-800">
                  <span className="font-bold text-[#49628b]">
                    Assurance vie universelle :
                  </span>{" "}
                  Police flexible avec potentiel de croissance de la valeur de
                  rachat, permettant des ajustements des primes et des garanties.
                </p>
              </div>
            </div>

            <h3 className="text-2xl sm:text-3xl font-bold text-[#49628b] text-center mt-10 sm:mt-16">
              Choisir la bonne police pour votre enfant
            </h3>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center max-w-5xl mx-auto mt-8">
              Lors de l'examen de l'assurance vie juvénile, il est essentiel
              d'évaluer les besoins de votre famille, vos objectifs financiers
              et les avantages que chaque type de police offre. Consulter un
              conseiller financier chez einsured.ca peut vous fournir des
              informations personnalisées adaptées à votre situation unique.
            </p>
          </div>
        </div>
      </section>

      {/* Investir + Contact */}
      <section className="py-12 sm:py-16 md:py-10 bg-white">
        <div className={container}>
          <div className="max-w-5xl mx-auto text-gray-800">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center">
              Investir dans l'avenir de votre enfant commence ici
            </h3>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center mt-6">
              Souscrire une police d'assurance vie sur votre enfant n'est
              peut-être pas la première chose qui vous vient à l'esprit lorsque
              vous pensez à protéger son avenir. Cependant, dans un monde en
              constante évolution, disposer d'un filet de sécurité offrant à la
              fois sécurité financière et potentiel de croissance future peut
              faire toute la différence.
            </p>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center mt-4">
              Commencez dès aujourd'hui en explorant comment l'assurance vie
              juvénile peut contribuer à l'avenir brillant et sécurisé de votre
              enfant. Contactez-nous pour des conseils personnalisés et pour
              trouver la police qui convient le mieux aux besoins de votre famille.
            </p>

            <h3 className="text-2xl sm:text-3xl font-bold text-[#49628b] text-center mt-10">
              Nous contacter
            </h3>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center mt-6">
              Prêt à passer à l'étape suivante ou avez-vous d'autres questions ?
              Notre équipe d'experts est là pour vous guider tout au long du
              processus, en vous fournissant les informations et le soutien dont
              vous avez besoin pour prendre la meilleure décision pour l'avenir
              de votre enfant.
            </p>

            <div className="text-center mt-6">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-sky-400 hover:bg-sky-500 text-white font-semibold text-lg px-10 py-4 rounded-full shadow-md transition"
              >
                Contactez-nous !
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Régime d'accidents étudiants */}
      <section className="relative py-12 sm:py-16 md:py-16 bg-[#e4ebf3] border-t-4 border-b-4 border-[#2b4672]">
        <div className={container}>
          <div className="max-w-6xl mx-auto text-gray-800">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6">
              3. Régime d'accidents étudiants
            </h2>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center max-w-5xl mx-auto">
              À mesure que les enfants grandissent et entament leurs parcours
              académiques, que ce soit à l'école primaire, secondaire ou
              universitaire, il est essentiel de prioriser leur bien-être et
              leur sécurité.
            </p>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center max-w-5xl mx-auto mt-4">
              Un Régime d'accidents étudiants offre une couverture complète
              conçue pour protéger les étudiants contre les événements imprévus
              susceptibles de perturber leurs études ou leur stabilité
              financière. Des urgences médicales aux circonstances imprévues
              affectant leur capacité à étudier, ce plan procure une tranquillité
              d'esprit aux étudiants et à leurs familles.
            </p>

            <h3 className="text-2xl sm:text-3xl font-bold text-[#49628b] text-center mt-10 sm:mt-12">
              Caractéristiques principales du Régime d'accidents étudiants
            </h3>

            <div className="mt-8 grid md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-[2rem] border border-gray-200 shadow-md">
                <div className="flex items-start gap-3">
                  <CheckCircle
                    aria-hidden="true"
                    className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                    strokeWidth={2.5}
                  />
                  <p className="text-gray-800">
                    <span className="font-bold text-[#49628b]">
                      Assurance accidents :
                    </span>{" "}
                    Protection financière en cas de blessures accidentelles,
                    couvrant les factures médicales, les frais de réadaptation
                    et même les prestations de décès accidentel.
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-[2rem] border border-gray-200 shadow-md">
                <div className="flex items-start gap-3">
                  <CheckCircle
                    aria-hidden="true"
                    className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                    strokeWidth={2.5}
                  />
                  <p className="text-gray-800">
                    <span className="font-bold text-[#49628b]">
                      Protection du revenu :
                    </span>{" "}
                    Couverture pour les pertes de revenus dues à une blessure
                    ou une maladie, assurant que les étudiants peuvent continuer
                    à remplir leurs obligations financières même s'ils sont
                    incapables de travailler.
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-[2rem] border border-gray-200 shadow-md">
                <div className="flex items-start gap-3">
                  <CheckCircle
                    aria-hidden="true"
                    className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                    strokeWidth={2.5}
                  />
                  <p className="text-gray-800">
                    <span className="font-bold text-[#49628b]">
                      Services d'assistance 24/7 :
                    </span>{" "}
                    Accès à des services d'assistance disponibles 24h/24, y
                    compris des conseils médicaux, une évacuation médicale
                    d'urgence et une assistance voyage, offrant aux étudiants
                    une tranquillité d'esprit où qu'ils se trouvent.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center max-w-6xl mx-auto mt-12">
              Un Régime d'accidents étudiants est bien plus qu'une simple
              assurance — c'est une solution complète conçue pour protéger les
              élèves contre les défis imprévus qu'ils peuvent rencontrer au cours
              de leur parcours académique. Avec une couverture médicale, une
              protection financière et des services de soutien adaptés à leurs
              besoins, ils peuvent se concentrer sur ce qui compte le plus : leur
              éducation. Qu'ils étudient localement ou à l'étranger, investir
              dans un Régime d'accidents étudiants garantit que chaque enfant
              dispose des ressources et du soutien nécessaires pour réussir.
            </p>

            <div className="text-center mt-8">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-sky-400 hover:bg-sky-500 text-white font-semibold text-lg px-10 py-4 rounded-full shadow-md transition"
              >
                Obtenir un devis
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Conclusion */}
      <section className="py-12 sm:py-16 md:py-16 bg-white">
        <div className={container}>
          <div className="max-w-5xl mx-auto text-gray-800 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-6">
              Conclusion :
            </h2>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 max-w-4xl mx-auto mb-8">
              Investir dans l'assurance pour enfants est une démarche proactive
              pour sécuriser l'avenir de votre enfant et lui offrir les meilleures
              opportunités possibles dans la vie. Qu'il s'agisse de se protéger
              contre l'imprévu, d'assurer la continuité des études ou de fournir
              une sécurité financière pour l'avenir, l'assurance pour enfants
              offre une tranquillité d'esprit aux parents et un filet de sécurité
              pour leurs biens les plus précieux — leurs enfants. Commencez à
              explorer vos options dès aujourd'hui et faites le premier pas vers
              la protection de l'avenir de votre enfant.
            </p>
            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-sky-400 hover:bg-sky-500 text-white font-semibold text-lg px-10 py-4 rounded-full shadow-md transition"
            >
              Commencer
            </a>
          </div>
        </div>
      </section>

      {/* Frequently Asked Questions */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 md:px-24 bg-white text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-5xl mx-auto text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#49628b]">
              Foire aux questions sur l'assurance pour enfants
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => {
              const expanded = activeIndex === index;
              const panelId = `faq-panel-${index}`;
              const btnId = `faq-btn-${index}`;

              return (
                <div
                  key={index}
                  className="border rounded-lg shadow-sm transition-all bg-white"
                >
                  <button
                    id={btnId}
                    className="w-full flex justify-between items-center text-left px-4 sm:px-6 py-3.5 sm:py-4 font-semibold text-base sm:text-lg text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1e48a0] focus-visible:ring-offset-2"
                    onClick={() => toggleFAQ(index)}
                    aria-expanded={expanded}
                    aria-controls={panelId}
                  >
                    <span>{faq.question}</span>

                    {expanded ? (
                      <Minus
                        className="w-6 h-6 text-gray-700 shrink-0 ml-4"
                        aria-hidden="true"
                      />
                    ) : (
                      <Plus
                        className="w-6 h-6 text-gray-700 shrink-0 ml-4"
                        aria-hidden="true"
                      />
                    )}
                  </button>

                  {expanded && (
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={btnId}
                      className="px-4 sm:px-6 pb-4 text-gray-700 text-sm sm:text-base leading-relaxed"
                    >
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <BlogSection title="Derniers articles" mode="related" relatedCount={3} />
    </>
  );
}
